import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import {
  AccountingDocType,
  AccountingDocumentEntity,
} from '../../../api/types/accounting/accounting-documents-entity';
import { CardBrandType } from '../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import globalPaymentServices from '../../../stores/global-payment-services';
import { formatDate } from '../../../utils/date-format';
import { AccountingDocumentsFilter } from './components/accounting-documents-filter';
import {
  AccountingDocumentsFilterType,
  defaultAccountingDocumentsFilter,
} from './accounting-documents-filter.type';
import { getAccountingDocumentsListOperation } from './operations/get-accounting-documents-list-operation';
import { exportAccountingDocumentsPdfOperation } from './operations/export-accounting-documents-pdf-operation';
import { exportAccountingDocumentsToExcelOperation } from './operations/export-accounting-documents-to-excel-operation';

// Max documents that can be selected at once for the merged-PDF export. Mirrors
// the deposit-details PDF export pattern (`export-deposit-receipts-pdf-operation`)
// but adds an explicit cap because this page is unbounded (no parent deposit),
// so we don't want a user to accidentally merge hundreds of PDFs in the browser.
const MAX_SELECTION_FOR_PDF_EXPORT = 100;

const docTypeLabel = (type: AccountingDocType): string => {
  switch (type) {
    case AccountingDocType.RECEIPT:
      return 'קבלה';
    case AccountingDocType.INVOICE:
      return 'חשבונית';
    case AccountingDocType.DONATION_RECEIPT:
      return 'קבלה לתרומה';
    case AccountingDocType.CASH_WITHDRAWAL:
      return 'שובר משיכה מקופה';
    default:
      return type;
  }
};

const docTypeSeverity = (
  type: AccountingDocType,
): 'success' | 'info' | 'warning' | 'danger' => {
  switch (type) {
    case AccountingDocType.RECEIPT:
      return 'success';
    case AccountingDocType.INVOICE:
      return 'info';
    case AccountingDocType.DONATION_RECEIPT:
      return 'warning';
    case AccountingDocType.CASH_WITHDRAWAL:
      return 'danger';
    default:
      return 'info';
  }
};

const documentSourceLabel = (row: AccountingDocumentEntity): string => {
  if (row.userPaymentId !== null) return 'סטודנט';
  if (row.crmPaymentId !== null || row.crmBillId !== null) return 'CRM';
  return 'חסר';
};

const documentBranchesLabel = (row: AccountingDocumentEntity): string => {
  if (!row.branches || row.branches.length === 0) return 'חסר';
  const names = row.branches
    .map((branch) => branch.branchName?.trim())
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : 'חסר';
};

const cardBrandLabel = (brand: CardBrandType): string => {
  switch (brand) {
    case CardBrandType.VISA:
      return 'Visa';
    case CardBrandType.MASTERCARD:
      return 'Mastercard';
    case CardBrandType.ISRACARD:
      return 'Isracard';
    case CardBrandType.AMERICAN_EXPRESS:
      return 'American Express';
    case CardBrandType.DINERS_CLUB:
      return 'Diners Club';
    case CardBrandType.DISCOVER:
      return 'Discover';
    case CardBrandType.JCB:
      return 'JCB';
    case CardBrandType.MAESTRO:
      return 'Maestro';
    case CardBrandType.PRIVATE_LABEL:
      return 'כרטיס פרטי';
    default:
      return brand;
  }
};

const creditCardCellContent = (row: AccountingDocumentEntity) => {
  const brand = row.creditCardBrand ?? null;
  const last4 = row.creditCardLast4?.trim() || null;
  if (!brand && !last4) {
    return 'חסר';
  }
  const brandText = brand ? cardBrandLabel(brand) : '';
  const last4Text = last4 ? `${last4}` : '';
  return [brandText, last4Text].filter(Boolean).join(' ');
};

export const AccountingDocumentsPage = observer(() => {
  const [data, setData] = useState<AccountingDocumentEntity[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<AccountingDocumentsFilterType>(
    defaultAccountingDocumentsFilter(),
  );
  const [appliedFilters, setAppliedFilters] =
    useState<AccountingDocumentsFilterType>(filters);

  // Selection for the merged-PDF export. PrimeReact uses `dataKey="id"` to
  // preserve selection across lazy pages, so this array can hold rows that
  // aren't currently visible (matches the deposit-details-dialog pattern).
  const [selectedDocs, setSelectedDocs] = useState<AccountingDocumentEntity[]>(
    [],
  );
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);

  // Monotonic request id used to discard out-of-order responses. When the user
  // changes a filter / paginates while a previous request is still in flight,
  // the older response would otherwise overwrite the newer one and the table
  // would render rows that don't belong to the latest filter (the original
  // bug we hit: switching paymentMethod still showed credit-card rows from
  // the previous unfiltered request).
  const searchSeqRef = useRef(0);

  useEffect(() => {
    if (globalPaymentServices.services.length === 0) {
      globalPaymentServices.fetchPaymentService().catch(() => {
        // store-level errors are not blocking for this page
      });
    }
  }, []);

  const runSearch = async (
    nextFilters: AccountingDocumentsFilterType,
    nextOffset: number,
    nextLimit: number,
  ) => {
    const seq = ++searchSeqRef.current;
    const isCurrent = () => seq === searchSeqRef.current;

    await getAccountingDocumentsListOperation(
      nextFilters,
      nextOffset,
      nextLimit,
      (next) => {
        if (isCurrent()) setData(next);
      },
      (next) => {
        if (isCurrent()) setTotal(next);
      },
      (next) => {
        if (isCurrent()) setLoading(next);
      },
    );
  };

  const handleSearch = async () => {
    setAppliedFilters(filters);
    setOffset(0);
    // Fresh data → fresh selection (selection is by id and would otherwise
    // be invisible to the user but still affect a subsequent export).
    setSelectedDocs([]);
    // Clear the previous result set immediately so the table does not show
    // rows that don't belong to the new filter while the request is loading.
    // For pagination we deliberately keep the existing rows visible under the
    // loading mask (smoother UX), but a filter change is a context switch and
    // showing stale rows there is misleading.
    setData([]);
    setTotal(0);
    await runSearch(filters, 0, limit);
  };

  const handleClear = () => {
    const reset = defaultAccountingDocumentsFilter();
    setFilters(reset);
    setSelectedDocs([]);
  };

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    // Selection is preserved across pages via `dataKey="id"` (see deposit
    // dialog for the same pattern). Do not reset it here.
    await runSearch(appliedFilters, newOffset, newLimit);
  };

  const handleSelectionChange = (nextSelection: AccountingDocumentEntity[]) => {
    // Intercept both single-row clicks and the header "select all on page"
    // shortcut so we never go past the cap. We reject the whole change
    // (rather than truncating) because truncation feels arbitrary to the
    // user when "select all" silently drops half the page.
    if (nextSelection.length > MAX_SELECTION_FOR_PDF_EXPORT) {
      toast.warn(
        `ניתן לבחור עד ${MAX_SELECTION_FOR_PDF_EXPORT} מסמכים בלבד לייצוא PDF`,
      );
      return;
    }
    setSelectedDocs(nextSelection);
  };

  const handleExportPdf = async () => {
    if (selectedDocs.length === 0 || exportingPdf || exportingExcel) return;
    await exportAccountingDocumentsPdfOperation(selectedDocs, setExportingPdf);
  };

  const handleExportExcel = () => {
    if (selectedDocs.length === 0 || exportingExcel || exportingPdf) return;
    exportAccountingDocumentsToExcelOperation(selectedDocs, setExportingExcel);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        direction: 'rtl',
      }}
    >
      <AccountingDocumentsFilter
        filters={filters}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            fontSize: '14px',
            color: '#4b5563',
          }}
        >
          <span style={{ textAlign: 'right' }}>
            סה״כ רשומות: {total.toLocaleString()}
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
              ניתן לבחור עד {MAX_SELECTION_FOR_PDF_EXPORT} מסמכים
            </span>
            <Button
              icon="pi pi-file-pdf"
              tooltip={
                selectedDocs.length > 0
                  ? `ייצוא ${selectedDocs.length} מסמכים לPDF מאוחד`
                  : 'יש לבחור מסמכים לייצוא לPDF'
              }
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              onClick={handleExportPdf}
              loading={exportingPdf}
              disabled={
                selectedDocs.length === 0 || exportingPdf || exportingExcel
              }
              style={{
                backgroundColor: '#dc2626',
                borderColor: '#dc2626',
                height: '32px',
              }}
            />
            <Button
              icon="pi pi-file-excel"
              tooltip={
                selectedDocs.length > 0
                  ? `ייצוא ${selectedDocs.length} מסמכים לאקסל`
                  : 'יש לבחור מסמכים לייצוא לאקסל'
              }
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              onClick={handleExportExcel}
              loading={exportingExcel}
              disabled={
                selectedDocs.length === 0 || exportingExcel || exportingPdf
              }
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
                height: '32px',
              }}
            />
          </div>
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          dataKey="id"
          selection={selectedDocs}
          onSelectionChange={(e) =>
            handleSelectionChange(e.value as AccountingDocumentEntity[])
          }
          tableStyle={{ direction: 'rtl' }}
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: '3rem' }}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column
            field="docNumber"
            header="מספר מסמך"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="docType"
            header="סוג מסמך"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => (
              <Tag
                value={docTypeLabel(row.docType)}
                severity={docTypeSeverity(row.docType)}
              />
            )}
          />
          <Column
            field="subject"
            header="מוען"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) =>
              row.subject?.trim() ? row.subject : 'חסר'
            }
          />
          <Column
            field="amount"
            header="סכום"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => {
              if (typeof row.amount !== 'number') return 'חסר';
              const abs = Math.abs(row.amount).toFixed(2);
              return row.amount < 0 ? `${abs}-` : abs;
            }}
          />
          <Column
            header="כרטיס אשראי"
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => creditCardCellContent(row)}
          />
          <Column
            header="מקור"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => documentSourceLabel(row)}
          />
          <Column
            header="שלוחות"
            style={{ textAlign: 'right', width: '180px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => documentBranchesLabel(row)}
          />
          <Column
            field="parentDocNumber"
            header="מסמך אב"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) => row.parentDocNumber ?? '—'}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />
          <Column
            header="קובץ"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDocumentEntity) =>
              row.fileUrl ? (
                <a
                  href={row.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563eb',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <i className="pi pi-external-link" />
                  פתח
                </a>
              ) : (
                <span style={{ color: '#9ca3af' }}>-</span>
              )
            }
          />
        </DataTable>
      </div>
    </div>
  );
});
